import { Dashboard } from "@/models/dashboard";
import { FiltersData } from "@/models/filter";
import { BackendWidget } from "@/models/widget-v2";
import { SimpleGridItem } from "@/hooks/use-simple-layout";
import logger from "@/utils/logger";

/**
 * Result of extracting dashboard data for the layout system.
 * Uses BackendWidget directly without intermediate conversion.
 */
export interface DashboardData {
  widgets: Record<string, BackendWidget>;
  layout: SimpleGridItem[];
  filters?: FiltersData;
}

/**
 * Extracts widgets and layout from backend widgets for the dashboard grid system.
 * 
 * Architecture: Widgets contain their own layout (x, y, w, h) and reference their parent dashboard.
 * This function creates a widgets map and layout array directly from BackendWidget data.
 * 
 * @param backendWidgets - Array of widgets from the API (already filtered by dashboard)
 * @param dashboard - Optional dashboard for filter parsing
 */
export function extractDashboardData(
  backendWidgets: BackendWidget[],
  dashboard?: Dashboard
): DashboardData {
  const widgets: Record<string, BackendWidget> = {};
  const layout: SimpleGridItem[] = [];

  // Parse filters if it's a string
  let filters: FiltersData | undefined;
  if (dashboard?.filters) {
    try {
      filters =
        typeof dashboard.filters === "string"
          ? JSON.parse(dashboard.filters)
          : dashboard.filters;
    } catch (e) {
      logger.error("Error parsing dashboard filters:", e);
    }
  }

  // Extract widgets - layout is stored directly in each widget
  backendWidgets.forEach((widget, index) => {
    const widgetId = widget.id || `widget_${index}`;
    
    // Store widget directly in the map
    widgets[widgetId] = {
      ...widget,
      id: widgetId,
    };

    // Layout is stored directly on the widget (x, y, w, h)
    layout.push({
      i: widgetId,
      x: widget.x ?? 0,
      y: widget.y ?? index,
      w: widget.w ?? 2,
      h: widget.h ?? 2,
    });
  });

  return { widgets, layout, filters };
}

/**
 * @deprecated Use extractDashboardData instead.
 * Alias for backward compatibility during migration.
 */
export function convertBackendWidgetsToFrontend(
  backendWidgets: BackendWidget[],
  dashboard?: Dashboard
): DashboardData {
  return extractDashboardData(backendWidgets, dashboard);
}
